import React, { useCallback, useEffect, useState } from 'react';
import { getOrgGuestRegistration, updateOrgGuestRegistration, getOrgLoyaltyPrograms } from '../../services/api';
import './GuestRegistrationPanel.css';

const errorOf = (err, fallback) => err?.response?.data?.error || fallback;

/* Регистрация в программе лояльности через бота (Telegram и MAX): гость,
   которого нет в iiko, указывает имя и дату рождения, даёт согласие на
   обработку ПД — и сервер заводит его в iiko и зачисляет в программу. */
const GuestRegistrationPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [programs, setPrograms] = useState(null);
  const [programsError, setProgramsError] = useState('');
  const [programsLoading, setProgramsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const apply = (d) => {
    setData(d);
    setForm({
      registration_enabled: d.settings.registration_enabled,
      loyalty_program_id: d.settings.loyalty_program_id || '',
      guest_privacy_url: d.settings.guest_privacy_url || '',
      guest_privacy_operator: d.settings.guest_privacy_operator || '',
    });
  };

  const load = useCallback(() => getOrgGuestRegistration().then(apply), []);
  useEffect(() => { load(); }, [load]);

  const loadPrograms = useCallback(async () => {
    setProgramsError(''); setProgramsLoading(true);
    try {
      setPrograms((await getOrgLoyaltyPrograms()).programs);
    } catch (err) {
      setProgramsError(errorOf(err, 'Не удалось получить программы из iiko'));
    } finally {
      setProgramsLoading(false);
    }
  }, []);

  useEffect(() => { if (data?.cloud_connected) loadPrograms(); }, [data?.cloud_connected, loadPrograms]);

  if (!data || !form) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setMessage(null);
    const program = (programs || []).find((p) => p.id === form.loyalty_program_id);
    try {
      apply(await updateOrgGuestRegistration({
        ...form,
        loyalty_program_name: program ? program.name : data.settings.loyalty_program_name,
      }));
      setMessage({ ok: true, text: 'Сохранено' });
    } catch (err) {
      setMessage({ ok: false, text: errorOf(err, 'Не удалось сохранить') });
    } finally {
      setBusy(false);
    }
  };

  const savedProgram = data.settings.loyalty_program_id;
  const programOptions = programs || (savedProgram
    ? [{ id: savedProgram, name: data.settings.loyalty_program_name || savedProgram, active: true }] : []);

  return (
    <form className="panel ui-settings-panel ui-form greg" onSubmit={submit}>
      <h4>Регистрация в программе лояльности через бота</h4>
      <p className="ui-settings-hint">
        Гостю, которого нет в iiko, бот предложит зарегистрироваться: имя, дата рождения (по желанию) и
        отдельное согласие на обработку персональных данных — после этого гость появится в iiko и в выбранной
        программе. Работает в Telegram и MAX, где номер подтверждает сам мессенджер; в VK — нет.
      </p>

      {!data.cloud_connected && (
        <div className="ui-inline-error">Сначала подключите iiko Cloud API — без него бот не может завести гостя.</div>
      )}

      <label className={`greg-toggle ${form.registration_enabled ? 'on' : ''}`}>
        <input type="checkbox" checked={form.registration_enabled} onChange={set('registration_enabled')}
               disabled={!data.cloud_connected} />
        <span>Предлагать гостям регистрацию</span>
      </label>

      <div className="ui-field">
        <label>Программа лояльности</label>
        <div className="greg-row">
          <select value={form.loyalty_program_id} onChange={set('loyalty_program_id')}>
            <option value="">— выберите —</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.active ? '' : ' (неактивна)'}</option>
            ))}
          </select>
          {data.cloud_connected && (
            <button type="button" className="ui-btn-ghost" onClick={loadPrograms} disabled={programsLoading}>
              {programsLoading ? 'Загружаем…' : 'Обновить'}
            </button>
          )}
        </div>
        {programsError && <div className="ui-hint-error">{programsError}</div>}
      </div>

      <div className="ui-field">
        <label>Политика обработки персональных данных</label>
        <input type="url" placeholder="https://…" value={form.guest_privacy_url} onChange={set('guest_privacy_url')} />
      </div>

      <div className="ui-field">
        <label>Оператор данных — кому гость даёт согласие</label>
        <input type="text" placeholder="ИП Иванов И. И. (ИНН 000000000000)" value={form.guest_privacy_operator}
               onChange={set('guest_privacy_operator')} />
        <div className="ui-settings-hint">
          Заведение, а не Gastronode: как в политике. Попадёт в текст согласия «я даю … согласие».
        </div>
      </div>

      <div className="greg-stats">
        {data.ready ? <span className="ui-status on">Регистрация работает</span>
          : <span className="ui-status">Выключена</span>}
        <span className="greg-count">Зарегистрировано через бота: <b>{data.registered}</b></span>
        {data.settings.guest_privacy_version && (
          <span className="greg-count">Редакция согласия от {data.settings.guest_privacy_version.split('-').reverse().join('.')}</span>
        )}
      </div>

      {message && <div className={message.ok ? 'ui-inline-success' : 'ui-inline-error'}>{message.text}</div>}
      <div className="ui-form-actions">
        <button type="submit" className="ui-btn-primary" disabled={busy}>Сохранить</button>
      </div>
    </form>
  );
};

export default GuestRegistrationPanel;
